import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ConfigService } from '@bo-client/config/config.service';
import { SKIP_ERROR_TOAST } from '@bo-client/core/http/http-context-tokens';
import { uri } from '@bo-client/shared/constants/uri';
import {
  AppNewsAdminModel,
  AppNewsLatestModel,
  AppNewsModel,
  AppNewsSaveModel
} from '@bo-client/shared/app-news/app-news.model';

/**
 * Zugriff auf die Neuigkeiten in netz-services (`/api/app-news`).
 *
 * Lesen (`getLatestPublishedAt`, `getPublished`) dürfen Sachbearbeiter und Superadmins, alles
 * andere nur Superadmins.
 */
@Injectable({ providedIn: 'root' })
export class AppNewsService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  private get baseUrl(): string {
    return `${this.configService.getSettings('serverUrl')}${uri.appNews}`;
  }

  /**
   * Zeitpunkt der zuletzt veröffentlichten Neuigkeit. Läuft bei jedem App-Start im Hintergrund;
   * ein Fehler soll den Sachbearbeiter nicht mit einem Toast stören.
   */
  getLatestPublishedAt(silent = false): Observable<AppNewsLatestModel> {
    return this.http.get<AppNewsLatestModel>(`${this.baseUrl}/latest-published-at`, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, silent)
    });
  }

  /** Veröffentlichte Neuigkeiten, zuletzt veröffentlichte zuerst. */
  getPublished(size: number, silent = false): Observable<AppNewsModel[]> {
    return this.http.get<AppNewsModel[]>(this.baseUrl, {
      params: new HttpParams().set('size', size),
      context: new HttpContext().set(SKIP_ERROR_TOAST, silent)
    });
  }

  /**
   * Verwaltung: alle Vorlagen und eine Seite der veröffentlichten; `page` und `size` wirken nur auf
   * die veröffentlichten.
   */
  getAll(page: number, size: number): Observable<AppNewsAdminModel> {
    return this.http.get<AppNewsAdminModel>(`${this.baseUrl}/admin`, {
      params: new HttpParams().set('page', page).set('size', size)
    });
  }

  create(news: AppNewsSaveModel): Observable<AppNewsModel> {
    return this.http.post<AppNewsModel>(this.baseUrl, news);
  }

  update(id: number, news: AppNewsSaveModel): Observable<AppNewsModel> {
    return this.http.put<AppNewsModel>(`${this.baseUrl}/${id}`, news);
  }

  publish(id: number): Observable<AppNewsModel> {
    return this.http.post<AppNewsModel>(`${this.baseUrl}/${id}/publish`, {});
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }
}
